/**
 * Client side of the `waadoo-platform` backend plugin
 * (plugins/waadoo-core-backend/src/plugins/waadooPlatform/plugin.ts), which feeds the
 * admin-only platform strip (`/overview`, PlatformStrip.tsx) and tells the
 * screens whether the caller is an admin (`/me`, see `useIsAdmin.ts`).
 *
 * The types are re-declared rather than imported, for the reason given in
 * `projectEnvironments/pipelines.ts`: they are the contract of `GET
 * /api/waadoo-platform/overview` and `/me`, to be changed on both sides at
 * once.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';

/** Mirrors the backend's `NodeAlertKind` (waadooPlatform/capacity.ts). */
export const NODE_ALERT_KINDS = [
  'notReady',
  'diskPressure',
  'memoryPressure',
  'pidPressure',
  'networkUnavailable',
  'cordoned',
] as const;
export type NodeAlertKind = (typeof NODE_ALERT_KINDS)[number];

export interface NodeAlert {
  kind: NodeAlertKind;
  /** The kubelet's own words for the condition, when it gives some. */
  message?: string;
}

/** One node; the optional fields are absent when the node does not state them. */
export interface NodeCapacity {
  name: string;
  /** `eu-west-par-a`. */
  zone?: string;
  /** The flavour, `b3-8` on OVH. */
  instanceType?: string;
  /** The node pool's name (`nodepool` label). */
  pool?: string;
  /** Its kubelet's version, `v1.35.2`. */
  kubeletVersion?: string;
  /** The machine's size (`status.capacity`), before the system reservations. */
  cpuCapacityMillis?: number;
  memCapacityBytes?: number;
  /** ISO 8601: when the node joined the cluster. */
  createdAt?: string;
  /** ISO 8601: since when the node is Ready — its uptime; absent if not Ready. */
  readySince?: string;
  /** What is wrong with it, most serious first; absent when nothing is. */
  alerts?: NodeAlert[];
  /** What is left to the pods (`status.allocatable`), what the bars measure. */
  cpuAllocatableMillis: number;
  cpuRequestedMillis: number;
  memAllocatableBytes: number;
  memRequestedBytes: number;
  /** The non-terminated pods bound to it, with or without requests. */
  pods: number;
}

/**
 * The cluster's software (backend `ClusterSoftware`, clusterInfo.ts): each
 * list the distinct values the nodes report, sorted, possibly empty.
 */
export interface ClusterSoftware {
  /** The API server's version, `v1.35.2`; absent when it could not be read. */
  kubernetesVersion?: string;
  kubeletVersions: string[];
  osImages: string[];
  kernelVersions: string[];
  /** Raw, `containerd://2.3.3` (`formatRuntime`). */
  containerRuntimes: string[];
  regions: string[];
}

/** One OVH node pool (backend `NodePool`, clusterInfo.ts). */
export interface NodePool {
  name: string;
  flavor?: string;
  autoscale: boolean;
  desiredNodes?: number;
  minNodes?: number;
  maxNodes?: number;
  currentNodes?: number;
  availableNodes?: number;
  upToDateNodes?: number;
}

export interface ClusterCapacity {
  nodes: number;
  cpuAllocatableMillis: number;
  cpuRequestedMillis: number;
  memAllocatableBytes: number;
  memRequestedBytes: number;
  perNode: NodeCapacity[];
  /** Absent when the answer carries none the page can read. */
  software?: ClusterSoftware;
  /** Absent when the backend could not list them (not an OVH cluster). */
  pools?: NodePool[];
  /** ISO 8601: when the backend read the cluster (cached up to 30 s). */
  readAt: string;
}

/** `cluster` is null when the backend could not read the cluster. */
export type PlatformOverview =
  | { admin: false }
  | { admin: true; cluster: ClusterCapacity | null };

export async function fetchPlatformOverview(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}): Promise<PlatformOverview> {
  // Absolute backend URL, as for the pipelines: in development the frontend
  // runs on another port.
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-platform');
  const response = await options.fetchApi.fetch(`${baseUrl}/overview`);
  if (!response.ok) {
    throw new Error(`Platform API: HTTP ${response.status}`);
  }
  return readPlatformOverview(await response.json());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A figure of the payload: a finite number, not below zero. */
const isFigure = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

const isCount = (value: unknown): value is number =>
  isFigure(value) && Number.isInteger(value);

const optionalString = (value: unknown) =>
  typeof value === 'string' && value !== '' ? value : undefined;

/** The four requested / allocatable figures of a node or of the cluster. */
const FIGURES = [
  'cpuAllocatableMillis',
  'cpuRequestedMillis',
  'memAllocatableBytes',
  'memRequestedBytes',
] as const;

/**
 * One node of the payload: dropped whole without its name, one of its four
 * figures or its pod count — the bars and the header line need them —, each
 * optional field dropped alone when it is wrong.
 */
/** One alert of the answer; undefined if not one this page knows. */
function readAlert(value: unknown): NodeAlert | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const kind = NODE_ALERT_KINDS.find(k => k === value.kind);
  if (!kind) {
    return undefined;
  }
  const message = optionalString(value.message);
  return message && message.length <= 500 ? { kind, message } : { kind };
}

function readNode(value: unknown): NodeCapacity | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const name = optionalString(value.name);
  const { pods } = value;
  if (!name || !isCount(pods)) {
    return undefined;
  }
  const node: NodeCapacity = {
    name,
    cpuAllocatableMillis: 0,
    cpuRequestedMillis: 0,
    memAllocatableBytes: 0,
    memRequestedBytes: 0,
    pods,
  };
  for (const field of FIGURES) {
    const figure = value[field];
    if (!isFigure(figure)) {
      return undefined;
    }
    node[field] = figure;
  }
  for (const field of [
    'zone',
    'instanceType',
    'pool',
    'kubeletVersion',
  ] as const) {
    const text = optionalString(value[field]);
    if (text) {
      node[field] = text;
    }
  }
  if (Array.isArray(value.alerts)) {
    const alerts = value.alerts
      .map(readAlert)
      .filter((alert): alert is NodeAlert => alert !== undefined);
    if (alerts.length > 0) {
      node.alerts = alerts;
    }
  }
  for (const field of ['createdAt', 'readySince'] as const) {
    const text = optionalString(value[field]);
    if (text && !Number.isNaN(Date.parse(text))) {
      node[field] = text;
    }
  }
  for (const field of ['cpuCapacityMillis', 'memCapacityBytes'] as const) {
    const figure = value[field];
    if (isFigure(figure)) {
      node[field] = figure;
    }
  }
  return node;
}

/** The non-empty strings of a list, the rest dropped; [] for a non-list. */
const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is string => typeof item === 'string' && item !== '',
      )
    : [];

const SOFTWARE_LISTS = [
  'kubeletVersions',
  'osImages',
  'kernelVersions',
  'containerRuntimes',
  'regions',
] as const;

/** The software of the payload; undefined when it is not an object. */
function readSoftware(value: unknown): ClusterSoftware | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const software: ClusterSoftware = {
    kubeletVersions: [],
    osImages: [],
    kernelVersions: [],
    containerRuntimes: [],
    regions: [],
  };
  for (const field of SOFTWARE_LISTS) {
    software[field] = stringList(value[field]);
  }
  const kubernetesVersion = optionalString(value.kubernetesVersion);
  if (kubernetesVersion) {
    software.kubernetesVersion = kubernetesVersion;
  }
  return software;
}

const POOL_COUNTS = [
  'desiredNodes',
  'minNodes',
  'maxNodes',
  'currentNodes',
  'availableNodes',
  'upToDateNodes',
] as const;

/** One pool of the payload: dropped without its name, a wrong count alone. */
function readPool(value: unknown): NodePool | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const name = optionalString(value.name);
  if (!name) {
    return undefined;
  }
  const pool: NodePool = { name, autoscale: value.autoscale === true };
  const flavor = optionalString(value.flavor);
  if (flavor) {
    pool.flavor = flavor;
  }
  for (const field of POOL_COUNTS) {
    const count = value[field];
    if (isCount(count)) {
      pool[field] = count;
    }
  }
  return pool;
}

/**
 * The cluster of the payload, null when its totals are wrong — the card
 * then says the figures are missing, as when the backend could not read
 * them. A wrong node is dropped alone.
 */
function readCluster(value: unknown): ClusterCapacity | null {
  if (
    !isRecord(value) ||
    !isCount(value.nodes) ||
    !Array.isArray(value.perNode) ||
    typeof value.readAt !== 'string'
  ) {
    return null;
  }
  const cluster: ClusterCapacity = {
    nodes: value.nodes,
    cpuAllocatableMillis: 0,
    cpuRequestedMillis: 0,
    memAllocatableBytes: 0,
    memRequestedBytes: 0,
    perNode: value.perNode
      .map(readNode)
      .filter((node): node is NodeCapacity => node !== undefined),
    readAt: value.readAt,
  };
  for (const field of FIGURES) {
    const figure = value[field];
    if (!isFigure(figure)) {
      return null;
    }
    cluster[field] = figure;
  }
  const software = readSoftware(value.software);
  if (software) {
    cluster.software = software;
  }
  if (Array.isArray(value.pools)) {
    cluster.pools = value.pools
      .map(readPool)
      .filter((pool): pool is NodePool => pool !== undefined);
  }
  return cluster;
}

/**
 * The answer of `GET /overview`, checked field by field. Throws when it
 * does not even say whether the caller is an admin: the page then shows no
 * card, as on any failure of the route (`useHomeData.ts`).
 */
export function readPlatformOverview(payload: unknown): PlatformOverview {
  if (!isRecord(payload) || typeof payload.admin !== 'boolean') {
    throw new Error('Platform API: unexpected answer');
  }
  return payload.admin
    ? { admin: true, cluster: readCluster(payload.cluster) }
    : { admin: false };
}

/** The answer of `GET /api/waadoo-platform/me`. */
export interface PlatformMe {
  admin: boolean;
}

/** Whether the caller is in the admin tier (`waadoo.adminGroups`). */
export async function fetchIsAdmin(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}): Promise<boolean> {
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-platform');
  const response = await options.fetchApi.fetch(`${baseUrl}/me`);
  if (!response.ok) {
    throw new Error(`Platform API: HTTP ${response.status}`);
  }
  return ((await response.json()) as PlatformMe).admin;
}

/**
 * The nodes the cluster counts as ready, for the « Nœuds » cell of the
 * platform strip: its node count minus the nodes whose `Ready`
 * condition is not True (the `notReady` alert of the backend's
 * `nodeAlerts`). A node the backend reports without a `Ready` condition at
 * all carries no alert, and counts as ready.
 */
export function readyNodes(cluster: ClusterCapacity): number {
  const notReady = cluster.perNode.filter(node =>
    node.alerts?.some(alert => alert.kind === 'notReady'),
  ).length;
  return Math.max(cluster.nodes - notReady, 0);
}

/**
 * From this percentage of the allocatable on, a bar turns to the warning
 * colour (amber); from `CRITICAL_PERCENT` on, to the danger colour (red).
 * Both compared with the percentage as the page shows it
 * (`displayedPercent`), so that the colour always matches the figure read
 * and the legend of the « Nœuds » drawer: « < 85 % », « 85–94 % »,
 * « ≥ 95 % ».
 */
export const WARNING_PERCENT = 85;
export const CRITICAL_PERCENT = 95;

/** Requested over allocatable; 0 when nothing is allocatable. */
export function usageRatio(requested: number, allocatable: number): number {
  return allocatable > 0 ? requested / allocatable : 0;
}

/**
 * A ratio as the whole percentage the page prints: the rounding of
 * `Intl.NumberFormat` with `style: 'percent'` and `maximumFractionDigits: 0`
 * — the formatter of every `percentText` the levels colour —, read back as a
 * number. Not `Math.round(ratio * 100)`, whose product can land on the other
 * side of a half: 0.285 * 100 is 28.499…, which `Math.round` makes 28 where
 * Intl prints « 29 % ». The rounding mode (`halfExpand`) does not depend
 * on the locale: only the digits and the sign of the result would, hence
 * a fixed `en-US` without grouping.
 */
const PERCENT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
  useGrouping: false,
});

export function displayedPercent(ratio: number): number {
  return Number(PERCENT.format(ratio).replace('%', ''));
}

export function isOverWarning(ratio: number): boolean {
  return displayedPercent(ratio) >= WARNING_PERCENT;
}

/** How full a bar is: below the warning line, past it, past the critical one. */
export type UsageLevel = 'ok' | 'warning' | 'critical';

export function usageLevel(ratio: number): UsageLevel {
  if (displayedPercent(ratio) >= CRITICAL_PERCENT) {
    return 'critical';
  }
  return isOverWarning(ratio) ? 'warning' : 'ok';
}

const LEVEL_ORDER: Record<UsageLevel, number> = {
  ok: 0,
  warning: 1,
  critical: 2,
};

/**
 * A node's level, the worse of its CPU and its memory (requested over
 * allocatable): the colour of its tile in the « Nœuds » drawer.
 */
export function nodeLevel(node: NodeCapacity): UsageLevel {
  const cpu = usageLevel(
    usageRatio(node.cpuRequestedMillis, node.cpuAllocatableMillis),
  );
  const memory = usageLevel(
    usageRatio(node.memRequestedBytes, node.memAllocatableBytes),
  );
  return LEVEL_ORDER[cpu] >= LEVEL_ORDER[memory] ? cpu : memory;
}

/** How long a node is « nouveau » after it joined the cluster. */
export const NEW_NODE_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a node joined the cluster (`createdAt`) less than `NEW_NODE_MS`
 * before `readAt` (when the backend read the cluster): what the autoscaler
 * just added, in practice. Not `readySince`, which a reboot or a kubelet
 * restart resets. A node without `createdAt` is not new.
 */
export function isNewNode(node: NodeCapacity, readAt: number): boolean {
  if (!node.createdAt) {
    return false;
  }
  return readAt - Date.parse(node.createdAt) < NEW_NODE_MS;
}

/** Millicores to cores, bytes to GiB: the units the card shows. */
export const toCores = (millis: number): number => millis / 1000;
export const toGiB = (bytes: number): number => bytes / 1024 ** 3;

/**
 * The short id of a node: what follows the last hyphen of its name, « 8xjnh »
 * for `ludo-mks-dev-k8s-0-pmd8p-8xjnh` — the part that tells apart the
 * nodes of one pool. A name without hyphen (or ending with one) is shown
 * whole.
 */
export function nodeShortId(name: string): string {
  return name.slice(name.lastIndexOf('-') + 1) || name;
}

/**
 * A node's memory as its flavour is sold: the capacity in GiB rounded to the
 * unit — a b3-8 of mks-ludo-mks-dev states ~7.7 GiB in `status.capacity`
 * and reads « 8 », OVH's figure for that flavour. Shown with the commercial
 * unit (« Go »), not the GiB of the bars.
 */
export const toSoldGiB = (bytes: number): number => Math.round(toGiB(bytes));

/** The nodes of one zone; `zone` absent for those that state none. */
export interface ZoneNodes {
  zone?: string;
  nodes: NodeCapacity[];
}

const byName = (a: string, b: string) => (a < b ? -1 : Number(a > b));

/**
 * The nodes by zone, the zones in order (`eu-west-par-a`, `-b`, `-c`), the
 * nodes without zone in a last group, each group's nodes by name. Plain
 * code-point order, the one the backend sorts the nodes in
 * (`computeCapacity`).
 */
export function groupNodesByZone(nodes: NodeCapacity[]): ZoneNodes[] {
  const groups = new Map<string | undefined, NodeCapacity[]>();
  for (const node of nodes) {
    const group = groups.get(node.zone);
    if (group) {
      group.push(node);
    } else {
      groups.set(node.zone, [node]);
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === undefined || b === undefined) {
        return Number(a === undefined) - Number(b === undefined);
      }
      return byName(a, b);
    })
    .map(([zone, group]) => ({
      ...(zone === undefined ? {} : { zone }),
      nodes: [...group].sort((a, b) => byName(a.name, b.name)),
    }));
}

/**
 * One zone of the « Nœuds » drawer: its nodes (`groupNodesByZone`), the
 * pools and flavours they state, their sums of requested and allocatable,
 * and the OVH pool the zone maps to one to one, when it does.
 */
export interface ZoneSummary extends ZoneNodes {
  /** The distinct `pool` of its nodes, sorted; the nodes without one left out. */
  poolNames: string[];
  /** The distinct `instanceType` of its nodes, sorted. */
  flavors: string[];
  cpuRequestedMillis: number;
  cpuAllocatableMillis: number;
  memRequestedBytes: number;
  memAllocatableBytes: number;
  /**
   * The pool whose nodes are all in this zone and nowhere else, when every
   * node of the zone states it; absent for an ambiguous zone (several pools,
   * a node without pool, a pool spanning zones, a pool OVH did not list) and
   * for the nodes without zone.
   */
  pool?: NodePool;
  /**
   * The places the autoscaler may still fill in this zone: the mapped
   * pool's `maxNodes` less the zone's nodes, when that pool is autoscaled
   * and states a maximum; undefined otherwise (no pips, no free rows).
   */
  freeSlots?: number;
}

/** The zones of the drawer, and whether the pools block is redundant. */
export interface ZoneLayout {
  zones: ZoneSummary[];
  /**
   * True when every pool maps to exactly one zone and every zone to one
   * pool — the zone headers then say all the pools block would: the drawer
   * leaves it out. False with no pool at all, or any ambiguity.
   */
  poolsMapped: boolean;
  /**
   * The free places summed over the zones — the dashed pips and « place
   * libre » rows, so the headroom line never contradicts them —, when
   * `poolsMapped` and at least one zone's pool is autoscaled with a maximum;
   * undefined otherwise (the pools block then says the headroom).
   */
  autoscalingMargin?: number;
}

const distinct = (values: (string | undefined)[]): string[] =>
  [...new Set(values.filter((v): v is string => v !== undefined))].sort(byName);

/**
 * The nodes by zone (`groupNodesByZone`) with their sums and the OVH pool
 * each zone maps to. On mks-ludo-mks-dev each pool lives in one zone
 * (k8s-0 in a, k8s-1 in b, k8s-2 in c), so the zones and the pools are the
 * same groups; this checks it rather than assumes it.
 */
export function zoneLayout(
  nodes: NodeCapacity[],
  pools: NodePool[] | undefined,
): ZoneLayout {
  const groups = groupNodesByZone(nodes);
  const poolsByName = new Map((pools ?? []).map(pool => [pool.name, pool]));
  // The zones each pool's nodes are in (`undefined` for no zone).
  const poolZones = new Map<string, Set<string | undefined>>();
  for (const node of nodes) {
    if (node.pool) {
      const zones = poolZones.get(node.pool) ?? new Set();
      zones.add(node.zone);
      poolZones.set(node.pool, zones);
    }
  }
  const zones = groups.map((group): ZoneSummary => {
    const poolNames = distinct(group.nodes.map(node => node.pool));
    const summary: ZoneSummary = {
      ...group,
      poolNames,
      flavors: distinct(group.nodes.map(node => node.instanceType)),
      cpuRequestedMillis: 0,
      cpuAllocatableMillis: 0,
      memRequestedBytes: 0,
      memAllocatableBytes: 0,
    };
    for (const node of group.nodes) {
      summary.cpuRequestedMillis += node.cpuRequestedMillis;
      summary.cpuAllocatableMillis += node.cpuAllocatableMillis;
      summary.memRequestedBytes += node.memRequestedBytes;
      summary.memAllocatableBytes += node.memAllocatableBytes;
    }
    const pool =
      poolNames.length === 1 ? poolsByName.get(poolNames[0]) : undefined;
    if (
      group.zone !== undefined &&
      pool &&
      group.nodes.every(node => node.pool === pool.name) &&
      poolZones.get(pool.name)?.size === 1
    ) {
      summary.pool = pool;
      if (pool.autoscale && pool.maxNodes !== undefined) {
        summary.freeSlots = Math.max(pool.maxNodes - group.nodes.length, 0);
      }
    }
    return summary;
  });
  const mapped = new Set(zones.map(zone => zone.pool?.name));
  const poolsMapped =
    poolsByName.size > 0 &&
    zones.every(zone => zone.pool !== undefined) &&
    [...poolsByName.keys()].every(name => mapped.has(name));
  const slots = zones
    .map(zone => zone.freeSlots)
    .filter((free): free is number => free !== undefined);
  return {
    zones,
    poolsMapped,
    ...(poolsMapped && slots.length > 0
      ? { autoscalingMargin: slots.reduce((sum, free) => sum + free, 0) }
      : {}),
  };
}

/**
 * A container runtime as the card shows it: `containerd://2.3.3` reads
 * « containerd 2.3.3 ». Any other form is shown as reported.
 */
export function formatRuntime(runtime: string): string {
  const match = /^([^:/]+):\/\/(.+)$/.exec(runtime);
  return match ? `${match[1]} ${match[2]}` : runtime;
}

/** The nodes that run one kubelet version (their full names, sorted). */
export interface KubeletGroup {
  version: string;
  nodes: string[];
}

/**
 * The nodes by kubelet version when the versions are MIXED — the kubelets
 * among themselves, or one of them against the API server's version (a
 * cluster midway through an upgrade); undefined when everything agrees, or
 * when no node reports a version. Versions in code-point order, the
 * nodes without one left out.
 */
export function kubeletMismatch(
  software: ClusterSoftware,
  nodes: NodeCapacity[],
): KubeletGroup[] | undefined {
  const versions = new Set(software.kubeletVersions);
  if (versions.size === 0) {
    return undefined;
  }
  if (software.kubernetesVersion) {
    versions.add(software.kubernetesVersion);
  }
  if (versions.size < 2) {
    return undefined;
  }
  const groups = new Map<string, string[]>();
  for (const node of nodes) {
    if (node.kubeletVersion) {
      groups.set(node.kubeletVersion, [
        ...(groups.get(node.kubeletVersion) ?? []),
        node.name,
      ]);
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => byName(a, b))
    .map(([version, names]) => ({ version, nodes: [...names].sort(byName) }));
}

/**
 * How far the autoscaled pools may grow: their current nodes against their
 * maximum, summed over the pools that are autoscaled and state a maximum;
 * undefined when there is none. A pool without `currentNodes` counts 0.
 */
export function autoscalingHeadroom(
  pools: NodePool[],
): { current: number; max: number } | undefined {
  const scaled = pools.filter(
    (pool): pool is NodePool & { maxNodes: number } =>
      pool.autoscale && pool.maxNodes !== undefined,
  );
  if (scaled.length === 0) {
    return undefined;
  }
  return {
    current: scaled.reduce(
      (total, pool) => total + (pool.currentNodes ?? 0),
      0,
    ),
    max: scaled.reduce((total, pool) => total + pool.maxNodes, 0),
  };
}

/**
 * The state of the whole platform, the first cell of the strip
 * (PlatformStrip.tsx): `failed` (« Dégradée ») when a node is not ready or a
 * component runs no pod at all; `degraded` (« À surveiller ») when a node
 * carries another alert (a pressure, a cordon), a pool has unavailable
 * nodes, the CPU or the memory requested shows `WARNING_PERCENT` or more, or a
 * component runs fewer pods than desired; `healthy` (« Saine ») otherwise.
 * `components` is the worst health of `componentsSummary`, undefined while
 * the components are not read (or could not be): the cluster alone decides
 * then.
 */
export function platformState(
  cluster: ClusterCapacity,
  components: 'healthy' | 'degraded' | 'failed' | undefined,
): 'healthy' | 'degraded' | 'failed' {
  if (readyNodes(cluster) < cluster.nodes || components === 'failed') {
    return 'failed';
  }
  const poolsShort = (cluster.pools ?? []).some(
    pool =>
      pool.currentNodes !== undefined &&
      pool.availableNodes !== undefined &&
      pool.availableNodes < pool.currentNodes,
  );
  if (
    cluster.perNode.some(node => (node.alerts?.length ?? 0) > 0) ||
    poolsShort ||
    isOverWarning(
      usageRatio(cluster.cpuRequestedMillis, cluster.cpuAllocatableMillis),
    ) ||
    isOverWarning(
      usageRatio(cluster.memRequestedBytes, cluster.memAllocatableBytes),
    ) ||
    components === 'degraded'
  ) {
    return 'degraded';
  }
  return 'healthy';
}
